<template>
  <div>
    <div class="asc-report-export">
      <div class="asc-report-export-options--container">
        <div class="asc-report-export-options">
          <asc-autocomplete-selector
            v-model="reportType"
            :items="reportTypesData"
            label="Report Type"
            class="asc-child-options"
            :on-change="handleReportNumberChange"
            placeholder="Select Report Type"
            no-array
            use-strings
            dense
            drop-down-size-matches
            required
            fixed-position
            short
            @input="handleInput()"
          ></asc-autocomplete-selector>
          <div class="asc-empty-div"></div>
          <asc-date-picker
            v-model="dates"
            class="asc-child-options"
            label="Dates"
            :max-days="maxRange"
            :max="maxDate"
            :has-icon="true"
            icon-position="right"
            range-only
            @input="handleInput()"
          ></asc-date-picker>
          <span class="asc-report-export--footer">
            Date Ranges are dependent on the report type selected above.
          </span>
          <template v-if="showCheckbox">
            <asc-checkbox
              v-model="isReducedApuList"
              class="asc-report-export--checkbox"
              :checked="isReducedApuList"
              label="Export reduced parameter list"
              @change="handleInput()"
            ></asc-checkbox>
          </template>
          <template v-else>
            <div class="asc-empty-div"></div>
          </template>
          <asc-autocomplete-selector
            v-model="airlineNames"
            :items="airlinesData"
            label="Airline"
            class="asc-child-options"
            placeholder="Select Airline"
            :component-type="airlinesData.length > 1 ? 'multiple' : 'single'"
            dense
            drop-down-size-matches
            fixed-position
            short
            @input="handleInput()"
          ></asc-autocomplete-selector>
          <div class="asc-empty-div"></div>
          <asc-autocomplete-selector
            v-model="tailNumbers"
            :items="tailsData"
            label="Tail"
            class="asc-child-options"
            placeholder="Select Tail"
            component-type="multiple"
            no-array
            dense
            drop-down-size-matches
            fixed-position
            @input="handleInput()"
          ></asc-autocomplete-selector>
          <div class="asc-empty-div"></div>
        </div>
        <asc-note class="asc-report-note">
          <v-icon class="asc-report-note--icon" :size="12" color="#C98703"
            >mdi-alert</v-icon
          >
          <span class="asc-report-note--text"
            >Note: Selected options will influence the amount of exported data
            and the time to generate the export.</span
          >
        </asc-note>
      </div>
    </div>
  </div>
</template>

<script>
import AscNote from '@/components/atoms/AscNote'
import AscAutocompleteSelector from './AscAutocompleteSelector'
import AscCheckbox from '@/components/atoms/AscCheckbox'
import AscDatePicker from '@/components/atoms/AscDatePicker'
import {
  getDateGivenOffset,
  getReportExportMinDate,
  getReportExportMaxDate,
} from '@/utilities/dates'

export default {
  name: 'AscReportExport',

  components: {
    AscAutocompleteSelector,
    AscCheckbox,
    AscNote,
    AscDatePicker,
  },

  props: {
    value: {
      type: Object,
      default: null,
    },
    reportTypes: {
      type: Array,
      default: () => [],
    },
    airlines: {
      type: Array,
      default: () => [],
    },
    tails: {
      type: Array,
      default: () => [],
    },
    pwcUser: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      reportType:
        this.value && this.value.reportType ? this.value.reportType : '',
      airlineNames:
        this.value && this.value.airlineNames
          ? this.value.airlineNames
          : this.airlines?.length === 1
          ? [this.airlines[0].value]
          : [],
      tailNumbers:
        this.value && this.value.tailNumbers ? this.value.tailNumbers : [],
      dates: {
        startDate: getDateGivenOffset(new Date(), -14),
        endDate: new Date(),
      },
      isReducedApuList: false,
      showCheckbox: false,
      minDate: getReportExportMinDate(),
      maxDate: getReportExportMaxDate(),
      maxRange: 14,
    }
  },

  computed: {
    reportTypesData() {
      return this.reportTypes
    },

    airlinesData() {
      return this.airlines
    },

    tailsData() {
      if (this.airlineNames.length === 0) {
        return []
      }

      const array = this.tails.filter(
        item =>
          this.airlineNames.filter(id => id === item.airlineId).length > 0,
      )
      return array
    },
  },

  mounted() {
    this.handleInput()
  },

  methods: {
    handleInput() {
      const data = {
        dates: {
          startDate:
            this.dates.startDate !== null
              ? this.dates.startDate
              : getDateGivenOffset(new Date(), -14),
          endDate:
            this.dates.endDate !== null ? this.dates.endDate : new Date(),
        },
        reportType: this.reportType
          ? this.reportType.split(' - ')[0].trim()
          : null,
        airlineNames: this.airlineNames,
        tailNumbers: this.tailNumbers,
        isReducedApuList: this.isReducedApuList,
      }
      this.$emit('input', data)
    },

    handleReportNumberChange(id, selection = null, selectedItems = []) {
      const reportNumber = selection ? selection.split(' - ')[0].trim() : null
      const isPerformanceReport = selectedItems
        ? selectedItems[0].isPerformanceReport
        : true

      const reducedParameterReport =
        reportNumber === '013' ||
        reportNumber === '017' ||
        reportNumber === '202'

      // Per 18491, checkbox should only show for the above 3 report types.
      if (reducedParameterReport) {
        this.showCheckbox = this.pwcUser
        this.isReducedApuList = this.pwcUser
        this.maxRange = 60
        this.dates = {
          startDate: getDateGivenOffset(new Date(), -30),
          endDate: new Date(),
        }
      } else if (!isPerformanceReport && !reducedParameterReport) {
        this.showCheckbox = false
        this.isReducedApuList = false
        this.maxRange = 60
        this.dates = {
          startDate: getDateGivenOffset(new Date(), -30),
          endDate: new Date(),
        }
      } else {
        this.showCheckbox = false
        this.isReducedApuList = false
        this.maxRange = 14
        this.dates = {
          startDate: getDateGivenOffset(new Date(), -14),
          endDate: new Date(),
        }
      }
      this.handleInput()
    },
  },
}
</script>

<style lang="scss">
.asc-report-export {
  padding: 20px 10px;

  & .asc-empty-div {
    height: 30px;
  }

  & .asc-report-note {
    &.asc-note--container {
      background-color: $semantic-warning-hover;
    }

    & .asc-note {
      display: flex;
    }

    &--icon {
      align-self: flex-start;
      padding: 6px 3px;
    }

    &--text {
      color: $primary-black;
      font-size: 12px;
      padding: 3px;
    }
  }

  &--footer {
    color: rgb(119, 119, 119);
    font-size: 12px;
    padding: 3px;
  }

  &--checkbox {
    margin: 15px 10px;

    label {
      padding-left: 4px !important;
      padding-bottom: 1px !important;
    }
  }
}
</style>
